// src/components/stages/preconsult/AssessmentSummaryRail.tsx
// Replaces PreConsultStage.tsx's old `LiveSummaryBar` — same data, same
// sticky/expand behaviour, restyled as the target mockup's labelled tile row
// instead of a collapsed `Label: value` badge chip row. See
// docs/HMS_V4_PRECONSULT_REDESIGN_PLAN.md Phase 1.4. Expanded state still
// opens the unchanged AssessmentSummaryCard.
import { useState } from 'react';
import AssessmentSummaryCard, { type AssessmentSummary, type TriageColor } from '../../AssessmentSummaryCard';
import { cn } from '../../../lib/cn';

const isEmptyValue = (v: unknown) => v === '' || v == null || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0);

const TONE_TEXT: Record<TriageColor, string> = {
  green: 'text-success-ink', yellow: 'text-warn-ink', red: 'text-danger-ink',
};

export interface AssessmentSummaryRailProps {
  summary: AssessmentSummary | null;
  title: string;
  /** False inside StageEditModal.tsx's amend popup — see LiveSummaryBar's
   * (this component's predecessor) own doc comment on why: that modal's
   * scrollable body is a short, fixed-height (82vh) box, not the full page
   * this sticky-strip pattern was designed for. */
  sticky?: boolean;
}

export function AssessmentSummaryRail({ summary, title, sticky = true }: AssessmentSummaryRailProps) {
  const [open, setOpen] = useState(false);
  if (!summary) return null;
  const { fields = [], due_checklist = [], overall } = summary;
  if (!fields.length && !due_checklist.length && !overall) return null;
  const dueNow = due_checklist.filter(d => d.color !== 'green');
  const shownFields = fields.filter(f => !isEmptyValue(f.value));

  if (open) {
    return (
      <div className={cn('z-10', sticky && 'sticky bottom-[58px]')}>
        <AssessmentSummaryCard summary={summary} title={title} onViewFull={() => setOpen(false)} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={cn(
        'z-10 w-full rounded-[var(--radius)] border border-warn bg-warn-soft px-3.5 py-2.5 text-left backdrop-blur',
        sticky && 'sticky bottom-[58px]'
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10.5px] font-extrabold uppercase tracking-wide text-warn-ink">⭐ {title}</span>
        <span className="ml-auto shrink-0 text-[11px] font-medium text-warn-ink">▾ expand</span>
      </div>
      {shownFields.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {shownFields.map(f => (
            <div key={f.key} className="min-w-[78px] rounded-lg border border-line bg-card px-3 py-1.5 text-center">
              <div className="truncate text-[9px] uppercase tracking-wide text-ink-muted">{f.label}</div>
              <div className={cn('mt-0.5 text-[12.5px] font-bold', TONE_TEXT[f.color])}>
                {Array.isArray(f.value) ? f.value.join(', ') : String(f.value)}
              </div>
            </div>
          ))}
        </div>
      )}
      {dueNow.length > 0 && (
        <div className="mt-2 rounded-lg bg-danger-soft px-2.5 py-1.5 text-[11.5px] font-semibold text-danger-ink">
          ⚠ Attention: {dueNow.map(d => d.label).join(' · ')} {dueNow.length === 1 ? 'is' : 'are'} due
        </div>
      )}
    </button>
  );
}
